import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useRef, useState } from 'react';
import { PasswordCaptureFields } from '../types';
import { Card, CardContent } from '@/components/ui/card';
import { VideoElement } from '@/components/video-player/VideoElement';
import { VideoFeatures } from '@/store/videoFeatures';
import { cn } from '@/lib/utils';

interface PasswordCapturePreviewProps {
  config: PasswordCaptureFields;
  videoUrl?: string | null;
  isLoading?: boolean;
}

export function PasswordCapturePreview({ config, videoUrl, isLoading = false }: PasswordCapturePreviewProps) {
  useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleTimeUpdate = () => {
    // Não precisa fazer nada no preview de senha
  };

  const handleDurationChange = () => {
    // Não precisa fazer nada no preview de senha
  };

  const handleVolumeChange = () => {
    const video = videoRef.current;
    if (video) {
      setIsMuted(video.muted);
    }
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Preview apenas - não faz nada
  };

  // Create VideoFeatures object for VideoElement
  const videoFeatures: VideoFeatures = {
    videoUrl: videoUrl,
    showControls: false,
    disableControls: false,
    autoPlay: false,
    loop: false,
    bigPlayButton: true,
    customThumbnail: null,
    initialThumbnail: null,
  };

  // Estilos do campo de senha (similar ao EmailCapturePreview)
  const fieldStyle = {
    backgroundColor: config.style.input.bg_color,
    color: config.style.input.text_color,
    borderRadius: `${config.style.input.border_radius}px`,
    padding: '0.75rem 1rem',
    fontSize: '0.85rem',
    height: '42px',
    outline: 'none',
    width: '100%',
  } as React.CSSProperties;

  const buttonStyle = {
    backgroundColor: config.style.button.bg_color,
    color: config.style.button.text_color,
    borderRadius: `${config.style.button.border_radius}px`,
    padding: '0.55rem 1.2rem',
    fontSize: '0.85rem',
    height: '42px',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
  } as React.CSSProperties;

  if (isLoading) {
    return (
      <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-sm overflow-hidden">
        <div className="aspect-video bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-500">{translateInterfaceText("Carregando vídeo...")}</p>
          </div>
        </div>
      </Card>
    );
  }

  if (!videoUrl) {
    return (
      <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="aspect-video bg-gray-50 flex items-center justify-center">
          <div className="text-center px-6">
            <p className="text-sm text-gray-500">{translateInterfaceText("Selecione um vídeo para visualizar o preview")}</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4 pb-6">
      <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.08)] overflow-hidden">
        <div className="p-0">
          {/* Container do Player - ASPECT RATIO 16:9 */}
          <div className="relative bg-[#0A0A0A] overflow-hidden w-full" style={{ aspectRatio: '16/9' }}>

            {/* Vídeo Real */}
            <div className="absolute inset-0 w-full h-full" key={videoUrl || 'default'}>
              <VideoElement
                videoRef={videoRef}
                features={videoFeatures}
                togglePlay={togglePlay}
                useCustomControls={false}
                isMuted={isMuted}
                showAutoplayOverlay={false}
                autoplayOverlayEnabled={false}
                onTimeUpdate={handleTimeUpdate}
                onDurationChange={handleDurationChange}
                onPlay={handlePlay}
                onPause={handlePause}
                onVolumeChange={handleVolumeChange}
              />
            </div>

            {/* Overlay de escurecimento (quando senha está ativa) */}
            {config.is_active && (
              <div
                className="absolute inset-0 transition-all duration-500 pointer-events-none"
                style={{
                  backgroundColor: config.style.container.bg_color,
                }}
              />
            )}

            {/* Formulário de Senha */}
            {config.is_active && (
              <section
                className="absolute flex flex-col items-center z-[666] left-1/2 -translate-x-1/2"
                style={{
                  bottom: 0,
                  height: '100%',
                  width: `${config.layout.width}px`,
                  maxWidth: '90%',
                  backgroundColor: 'transparent',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                }}
              >
                {/* Título */}
                {config.content.title.text && (
                  <h6
                    className="text-center mb-2"
                    style={{
                      fontSize: '1rem',
                      color: config.style.text.title_color,
                      width: '95%',
                      fontWeight: 'normal',
                      marginBottom: '0.5rem',
                      lineHeight: '1.3',
                    }}
                  >
                    {config.content.title.text}
                  </h6>
                )}

                {/* Container do formulário */}
                <div
                  className="flex justify-between flex-col"
                  style={{
                    width: '100%',
                  }}
                >
                  {/* Form */}
                  <form
                    onSubmit={handlePasswordSubmit}
                    className="flex flex-col"
                  >
                    {/* Campo de senha */}
                    <div className="mb-3">
                      <input
                        type="password"
                        placeholder={config.content.password.placeholder}
                        disabled
                        style={fieldStyle}
                      />
                    </div>

                    {/* Botão de submit */}
                    <button
                      type="submit"
                      className="hover:brightness-110"
                      style={{
                        ...buttonStyle,
                        width: '100%',
                      }}
                    >
                      {config.content.button_label}
                    </button>
                  </form>
                </div>

                {/* Rodapé */}
                {config.content.footer && (
                  <h6
                    className="text-center mt-2.5"
                    style={{
                      fontSize: '0.85rem',
                      color: config.style.text.body_color,
                      width: '95%',
                      lineHeight: '150%',
                      fontWeight: 'normal',
                      marginTop: '10px',
                      marginBottom: 0,
                    }}
                  >
                    {config.content.footer}
                  </h6>
                )}
              </section>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
